import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ProfileStatus } from "@edp-models/profile-status.model";
import { EdpResponseBody } from "@edp-models/response.model";
import { IUserNotification, UserNotification, UserNotificationFactory } from "@edp-models/user-notification.model";
import { EdpUser } from "@edp-models/user.model";
import { UserService } from "@edp-services/user.service";
import {
    EUserActions,
    GetProfileStatus,
    SetCebrationZs,
    SetProfileStatus,
    SetUser,
    SetViewToastLocalReception,
    UserNotificationsAPIError,
    UserNotificationsMarkReaded,
    UserNotificationsMarkReadedPersist,
    UserNotificationsMarkViewed,
    UserNotificationsMarkViewedPersist,
    UserNotificationsSetList,
    UserNotificationsSetTotalNotViewed,
} from "@edp-store/actions/user.actions";
import { getContracts } from "@edp-store/selectors/contract.selectors";
import { getUserNotificationsTotalNotViewed } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";

@Injectable()
export class UserEffects {
    @Effect()
    loadUpdateHasWelcome$ = this.actions$.pipe(
        ofType(EUserActions.UserWelcomeViewed),
        switchMap(() => this.userService.welcomeViewed()),
        switchMap((user: EdpUser) => {
            return of(new SetUser(user));
        }),
        tap(() => this.router.navigate(["/planeta-zero/inscricao"]))
    );

    @Effect()
    loadHasEdpE$ = this.actions$.pipe(
        ofType(EUserActions.UserEdpESubscribed),
        switchMap((code: string) => this.userService.edpESubscribed(code)),
        switchMap((user: EdpUser) => {
            return of(new SetUser(user));
        }),
        tap(() => this.router.navigate(["/energia/inicio"]))
    );

    @Effect()
    loadEdpESub$ = this.actions$.pipe(
        ofType(EUserActions.UserEdpESubViewed),
        switchMap(() => this.userService.edpESubViewed()),
        switchMap((user: EdpUser) => {
            return of(new SetUser(user));
        }),
        tap(() => this.router.navigate(["/energia/inicio"]))
    );

    @Effect()
    loadNotificationsList$ = this.actions$.pipe(
        ofType(EUserActions.UserNotificationsGetAll),
        switchMap(() => this.userService.getAll().pipe(catchError((error: Error) => of(new UserNotificationsAPIError(error))))),
        withLatestFrom(this.store.select(getContracts)),
        mergeMap(([notifications, contracts]) => {
            if (notifications instanceof UserNotificationsAPIError) {
                return of(notifications);
            } else {
                const notificationList: UserNotification[] = (notifications as IUserNotification[]).map(notification => {
                    notification.Contract = contracts.find(contract => contract.ContractId === notification.ContractId);
                    return UserNotificationFactory.create(notification);
                });

                return of(new UserNotificationsSetList(notificationList), new UserNotificationsSetTotalNotViewed());
            }
        })
    );

    @Effect()
    markReaded$ = this.actions$.pipe(
        ofType(EUserActions.MarkNotificationReaded),
        switchMap((action: UserNotificationsMarkReaded) =>
            this.userService.markRead(action.payload.Id.toString()).pipe(
                // switch to error action
                catchError((error: Error) => of(new UserNotificationsAPIError(error))),
                map((result: boolean | Error) => {
                    if (result instanceof Error) {
                        return result;
                    } else {
                        return result ? action.payload : false;
                    }
                })
            )
        ),
        switchMap((notification: false | UserNotification) => {
            if (notification instanceof UserNotificationsAPIError) {
                return of(notification);
            } else if (notification !== false) {
                // dispache success actions
                return of(new UserNotificationsMarkReadedPersist(notification), new UserNotificationsSetTotalNotViewed());
            }

            return of(notification);
        })
    );

    @Effect()
    markViewed$ = this.actions$.pipe(
        ofType(EUserActions.MarkNotificationViewed),
        withLatestFrom(this.store.select(getUserNotificationsTotalNotViewed)),
        switchMap(([action, notViewed]: [UserNotificationsMarkViewed, number]) => {
            if (notViewed > 0) {
                // call service
                return this.userService.markAllViewed().pipe(
                    // switch to error action
                    catchError((error: Error) => of(new UserNotificationsAPIError(error)))
                );
            } else {
                // we don't need to call the service, since
                // there's no unviewed notifications
                return of(true);
            }
        }),
        switchMap(notification => {
            if (notification === true) {
                // dispache success actions
                return of(new UserNotificationsMarkViewedPersist(), new UserNotificationsSetTotalNotViewed());
            }

            return of(notification);
        })
    );

    @Effect()
    setUserLevelStatus$ = this.actions$.pipe(
        ofType(EUserActions.MarkLevelModalAsRead),
        switchMap(() =>
            this.userService.setUserLevelStatus(false).pipe(
                switchMap(() => EMPTY),
                catchError(() => EMPTY)
            )
        )
    );

    @Effect()
    markPlanetAnniversaryAsRead$ = this.actions$.pipe(
        ofType(EUserActions.MarkPlanetAnniversaryAsRead),
        switchMap(() =>
            this.userService.markPlanetAnniversaryAsRead().pipe(
                switchMap(() => [{ type: EUserActions.SetPlanetAnniversary, payload: 0 }]),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    markVideoOnboardingAsViewed$ = this.actions$.pipe(
        ofType(EUserActions.MarkVideoOnboardingAsViewed),
        switchMap(() =>
            this.userService.markVideoOnboardingAsViewed().pipe(
                switchMap(() => EMPTY),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    getCounter$ = this.actions$.pipe(
        ofType(EUserActions.GetCounter),
        switchMap((action: any) => this.userService.getCounter().pipe(catchError((error: Error) => EMPTY))),
        switchMap((data: any) => {
            if (!data) {
                return;
            }
            const { points } = data.Result.Collection;
            return of(new SetCebrationZs(points));
        })
    );

    @Effect()
    setViewToastLocalReception$ = this.actions$.pipe(
        ofType(EUserActions.SetViewToastLocalReception),
        switchMap((action: SetViewToastLocalReception) =>
            this.userService.setViewToastLocalReception(action.payload).pipe(
                switchMap(() => EMPTY),
                catchError((error: Error) => EMPTY)
            )
        )
    );

    @Effect()
    getProfileStatus$ = this.actions$.pipe(
        ofType(EUserActions.GetProfileStatus),
        switchMap((action: GetProfileStatus) => this.userService.getProfileStatus().pipe(catchError((error: Error) => EMPTY))),
        switchMap((data: EdpResponseBody) => {            
            if (!data) {
                return;
            }
            const profileStatus = data.Result.Collection;
            return of(new SetProfileStatus(new ProfileStatus(profileStatus)));
        })
    );

    constructor(private actions$: Actions, private userService: UserService, private store: Store<IAppState>, private router: Router) {}
}
